import Lottie from "lottie-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlinePlayArrow } from "react-icons/md";
import { useDispatch } from "react-redux";
import Wave from "react-wavify";

import { ReactComponent as HomeworksStudent } from "../../../assets/homeworks_student.svg";
import animationData from "../../../assets/json/Comp.json";
import lesson_challenge from "../../../assets/lesson_challenge.svg";
import lesson_game from "../../../assets/lesson_game.svg";
import lesson_locked from "../../../assets/lesson_locked.svg";
import lesson_project from "../../../assets/lesson_project.svg";
import lesson_placeholder from "../../../assets/maxresdefault.jpeg";
import { ReactComponent as QuizzesStudent } from "../../../assets/quizzes_student.svg";
import WhiteCurl from "../../../assets/white_curl.svg";
import LoadingSpinner from "../../../components/LoadingSpinner";
import SpinWheelModal from "../../../components/modals/student/SpinWheelModal";
import { IDE_TYPE, USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import { setUser } from "../../../redux/slices/userSlice";
import lessonServices from "../../../services/lessonServices";
import studentServices from "../../../services/studentServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const LearningCard = ({
  openSpinWheelModal,
  navigate,
  type,
  title,
  isLocked,
  isActive,
  isFinished,
  isCompleted,
  isOrdered,
  has_recap_video,
  image,
  id,
  lastIsActiveLesson,
  courseName,
  challengeDetails,
  prevLesson,
  numberLabel,
}) => {
  const displayType = type ? type.charAt(0).toLowerCase() + type.slice(1) : "";
  const scrollRef = useRef(null);
  const { t } = useTranslation();

  const startLesson = async () => {
    const res = await lessonServices.startLesson(id);
  };

  const backgroundImage = image ? `url(${image})` : `url(${lesson_placeholder})`;

  useEffect(() => {
    if (lastIsActiveLesson?.lesson?.id === id && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [lastIsActiveLesson, id]);

  // Render buttons based on type and states
  const renderButtons = () => {
    const sharedButtonClass =
      "mt-2 border-[#96B8F3] bg-blue-500 w-full rounded-3xl p-4 font-semibold text-white";

    const canStartLesson =
      !isOrdered && !prevLesson?.is_locked && (prevLesson?.is_completed || prevLesson?.finished_by_teacher);

    const shouldShowDisabled =
      !isOrdered && !prevLesson?.is_locked && !prevLesson?.is_completed && !prevLesson?.finished_by_teacher;

    if (isLocked && isOrdered) {
      return (
        <button
          type="button"
          className={`${sharedButtonClass} disabled aria-disabled="true" wavy-btn relative overflow-hidden border-2 !border-blue-800 !bg-transparent !text-blue-800`}
          disabled
          aria-disabled="true"
        >
          {t("locked")}
        </button>
      );
    }
    if (isCompleted) {
      return (
        <button
          onClick={() => {
            startLesson();
            if (type === "challenge") {
              openSpinWheelModal(id);
            } else if (type === "graduation") {
              navigate(
                `/${USER_TYPE.STUDENT.ROLE}/coding-area/graduation/${id}`,
                {},
                {
                  lessonName: title,
                }
              );
            } else if (type === "project") {
              navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/levels-project/${id}`);
            } else if (type === "game") {
              navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-game/${id}`);
            } else {
              navigate(
                `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-video/${id}/${type}`,
                {},
                {
                  lessonName: title,
                  courseName: courseName,
                }
              );
            }
          }}
          className={`${sharedButtonClass} wavy-btn group relative border-4 !border-[#e0ffc6] !bg-[#6AB131]`}
        >
          <span className="absolute left-1/2 top-[-30px] w-max -translate-x-1/2 scale-0 rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
            {t("click_to_revisit_the_lesson")}
          </span>
          <span className="absolute left-[6px] top-[6px]">
            <img src={WhiteCurl} className="w-5" alt="" />
          </span>
          <div className="flex items-center justify-center">
            <h5>
              {t("completed")}{" "}
              {type === "challenge" &&
                `${challengeDetails?.student_total_easy + challengeDetails?.student_total_hard || 0} / ${challengeDetails?.total_easy + challengeDetails?.total_hard || 0} `}
            </h5>
          </div>
        </button>
      );
    }
    if (canStartLesson || shouldShowDisabled) {
      return (
        <button
          onClick={
            canStartLesson
              ? () => {
                  startLesson();
                  if (type === "challenge") {
                    openSpinWheelModal(id);
                  } else if (type === "graduation") {
                    navigate(
                      `/${USER_TYPE.STUDENT.ROLE}/coding-area/graduation/${id}`,
                      {},
                      {
                        lessonName: title,
                      }
                    );
                  } else if (type === "project") {
                    navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/levels-project/${id}`);
                  } else if (type === "game") {
                    navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-game/${id}`);
                  } else {
                    navigate(
                      `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-video/${id}/${type}`,
                      {},
                      {
                        lessonName: title,
                        courseName: courseName,
                      }
                    );
                  }
                }
              : undefined // disabled state – no click
          }
          className={`${sharedButtonClass} wavy-btn relative border-4 ${
            !canStartLesson
              ? "disabled overflow-hidden !border-2 !border-blue-800 !bg-transparent !text-blue-800"
              : ""
          }`}
          disabled={!canStartLesson}
        >
          {!canStartLesson ? null : (
            <span className="absolute left-[6px] top-[6px]">
              <img src={WhiteCurl} className="w-5" alt="" />
            </span>
          )}
          {type === "challenge" || type === "project" ? t("accept_the_challenge") : t("start")}{" "}
          {!canStartLesson
            ? null
            : type === "challenge" && (
                <span>
                  {(challengeDetails?.student_total_easy ?? 0) + (challengeDetails?.student_total_hard ?? 0)}/
                  {(challengeDetails?.total_easy ?? 0) + (challengeDetails?.total_hard ?? 0)}
                </span>
              )}
        </button>
      );
    }
    if (isLocked) {
      return (
        <button
          type="button"
          className={`${sharedButtonClass} disabled aria-disabled="true" wavy-btn relative overflow-hidden border-2 !border-blue-800 !bg-transparent !text-blue-800`}
          disabled
          aria-disabled="true"
        >
          {t("locked")}
        </button>
      );
    }
    if (isFinished) {
      return (
        <button
          onClick={() => {
            startLesson();
            if (type === "challenge") {
              openSpinWheelModal(id);
            } else if (type === "graduation") {
              navigate(
                `/${USER_TYPE.STUDENT.ROLE}/coding-area/graduation/${id}`,
                {},
                {
                  lessonName: title,
                }
              );
            } else if (type === "project") {
              navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/levels-project/${id}`);
            } else if (type === "game") {
              navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-game/${id}`);
            } else {
              navigate(
                `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-video/${id}/${type}`,
                {},
                {
                  lessonName: title,
                  courseName: courseName,
                }
              );
            }
          }}
          className={`${sharedButtonClass} wavy-btn relative border-4 !border-[#ffe4af] bg-orange-500`}
        >
          <span className="absolute left-[6px] top-[6px]">
            <img src={WhiteCurl} className="w-5" alt="" />
          </span>
          <div className="flex items-center justify-center">
            <span>{t("finished")}</span>
            <span className="mx-2 h-6 border-r-2"></span>
            <span>
              <p> {type === "challenge" || type === "project" ? t("accept_the_challenge") : t("start")} </p>
            </span>
          </div>
        </button>
      );
    }
    if (!isActive) {
      return (
        <button
          type="button"
          className={`${sharedButtonClass} disabled aria-disabled="true" wavy-btn relative overflow-hidden border-2 !border-blue-800 !bg-transparent !text-blue-800`}
          disabled
          aria-disabled="true"
        >
          {type === "challenge" || type === "project" ? t("accept_the_challenge") : t("start")}{" "}
        </button>
      );
    }
    const typeConfigs = {
      lesson: {
        onClick: () =>
          navigate(
            `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-video/${id}/${type}`,
            {},
            {
              lessonName: title,
              courseName: courseName,
            }
          ),
        style: "",
      },
      challenge: {
        progress: `${challengeDetails?.student_total_easy + challengeDetails?.student_total_hard || 0}/${challengeDetails?.total_easy + challengeDetails?.total_hard || 0}`,
        onClick: () => openSpinWheelModal(id),
        style: "",
      },
      code: {
        onClick: () =>
          navigate(
            `/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-video/${id}/${type}`,
            {},
            {
              lessonName: title,
              courseName: courseName,
            }
          ),
        style: "",
      },
      graduation: {
        onClick: () =>
          navigate(
            `/${USER_TYPE.STUDENT.ROLE}/coding-area/graduation/${id}`,
            {},
            {
              lessonName: title,
            }
          ),
        style: "",
      },
      game: {
        onClick: () => navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/lesson-game/${id}`),
        style: "",
      },
      project: {
        onClick: () => navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area/levels-project/${id}`),
        style: "",
      },
    };
    const config = typeConfigs[type];

    if (!config) return null;

    return (
      <button
        onClick={() => {
          config.onClick();
          startLesson();
        }}
        className={`${sharedButtonClass} wavy-btn relative border-4 ${config.style}`}
      >
        <span className="absolute left-[6px] top-[6px]">
          <img src={WhiteCurl} className="w-5" alt="" />
        </span>
        {type === "challenge" || type === "project" ? t("accept_the_challenge") : t("start")}{" "}
        {config?.progress}
      </button>
    );
  };

  const canStartLesson =
    !isOrdered && !prevLesson?.is_locked && (prevLesson?.is_completed || prevLesson?.finished_by_teacher);

  const shouldShowDisabled =
    !isOrdered && !prevLesson?.is_locked && !prevLesson?.is_completed && !prevLesson?.finished_by_teacher;

  const isLockedOrInactive = isLocked || (!isActive && !isCompleted);

  const shouldBeGray = shouldShowDisabled || isLockedOrInactive;
  return (
    <div className="flex w-full flex-col items-start">
      <div
        className={`font-light leading-5 ${canStartLesson ? "text-blue-500" : shouldBeGray ? "text-[#848484]" : "text-blue-500"}`}
      >
        {t(displayType)} {numberLabel}
        {isOrdered && ":"}
      </div>

      <div
        className={`mb-2 text-lg font-bold ${canStartLesson ? "text-orange-500" : shouldBeGray ? "text-[#949494]" : "text-orange-500"}`}
      >
        {title}
      </div>

      <div
        ref={lastIsActiveLesson?.lesson?.id === id ? scrollRef : null}
        style={{ backgroundImage }}
        className={`relative h-48 w-full rounded-3xl bg-white bg-cover bg-center ${lastIsActiveLesson?.lesson?.id == id ? "shake-animation border-[6px] border-blue-500" : "border-4 border-blue-500"} `}
      >
        {/* Image at the bottom center */}
        {type == "challenge" || type == "game" || type == "project" ? (
          <>
            <div className="absolute left-[-20px] top-[-12px] z-50 h-14 w-14 rounded-full bg-blue-500 px-4 pt-2"></div>
            <img
              src={
                type == "challenge"
                  ? lesson_challenge
                  : type == "game"
                    ? lesson_game
                    : type == "project"
                      ? lesson_project
                      : null
              } // <-- Replace with your image URL
              alt="Bottom Center"
              className="absolute left-[-16px] top-[-12px] z-50 w-12"
            />
          </>
        ) : null}

        {!canStartLesson && shouldShowDisabled ? null : canStartLesson && !shouldShowDisabled ? null : (
          <>
            {isLocked && (
              <div className="absolute inset-0 z-40 flex items-center justify-center">
                <div
                  className="absolute inset-0 z-40 bg-[#1C3674]/70 backdrop-blur-sm"
                  style={{ borderRadius: "20px" }}
                ></div>
                <img src={lesson_locked} alt="Locked" className="relative z-50 h-20 w-20" />
              </div>
            )}
          </>
        )}

        {has_recap_video && (
          <div className="absolute bottom-2 right-2 flex rounded-full bg-blue-500 px-2 py-1 text-xs text-white">
            <span>{t("recap_video")}</span>
            <span>
              <MdOutlinePlayArrow size={18} />
            </span>
          </div>
        )}
      </div>

      {renderButtons()}
    </div>
  );
};

const StudentLearningArea = () => {
  const navigate = useNavigateWithLang();
  const { t } = useTranslation();
  const lang = getLanguage();
  const spinWheelModalRef = useRef();
  const user = useAuth();
  let numberCount = 0;
  const [loading, setLoading] = useState(false);
  const [studentLessons, setStudentLessons] = useState([]);
  const [courseDetails, setCourseDetails] = useState([]);
  const [lastIsActiveLesson, setLastIsActiveLesson] = useState("");
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [animatedHeight, setAnimatedHeight] = useState(0);
  const [loadingSpinStats, setLoadingSpinStats] = useState(false);
  const dispatch = useDispatch();

  const openSpinWheelModal = async (ID) => {
    setLoadingSpinStats(true);
    try {
      const response = await studentServices.getStudentChallengeStats(ID);
      setLoadingSpinStats(false);

      spinWheelModalRef.current?.openModal(ID, response);
    } catch (error) {
      setLoadingSpinStats(false);
      console.error("Failed to fetch course details:", error);
    }
  };

  const handleSpinWheelSuccess = () => {};

  const mapIdeType = (ide) => {
    if (!ide) return IDE_TYPE.SMOOTHY;

    const normalized = ide.toLowerCase();
    if (normalized.includes("web")) return IDE_TYPE.WEB;
    if (normalized.includes("python")) return IDE_TYPE.PYTHON;
    return IDE_TYPE.SMOOTHY;
  };

  const fetchCourseDetails = async () => {
    try {
      const response = await studentServices.getStudentCourses();
      setCourseDetails(response);

      dispatch(
        setUser({
          ...user,
          IDEType: mapIdeType(response?.ide),
        })
      );
    } catch (error) {
      console.error("Failed to fetch course details:", error);
    }
  };

  const fetchStudentLessons = async () => {
    setLoading(true);
    try {
      const response = await studentServices.getStudentLessons();
      setStudentLessons(response);
      const { total_completed_lessons, number_of_lessons } = response;

      if (number_of_lessons > 0) {
        const percent = Math.round((total_completed_lessons / number_of_lessons) * 100);
        setCompletionPercentage(percent);
      }

      const lastActiveLesson = response.completion
        ?.slice()
        .reverse()
        .find(
          (lesson) =>
            !lesson.is_complete && !lesson.finished_by_teacher && !lesson.is_locked && lesson.is_active
        );

      setLastIsActiveLesson(lastActiveLesson);
    } catch (error) {
      console.error("Failed to fetch student lessons:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudentLessons();
    fetchCourseDetails();
  }, [lang]);

  useEffect(() => {
    // Trigger animation after mount
    const timeout = setTimeout(() => {
      setAnimatedHeight(completionPercentage);
    }, 200); // slight delay for smooth effect

    return () => clearTimeout(timeout);
  }, [completionPercentage]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!studentLessons?.completion?.length) {
    return <div className="flex min-h-screen items-center justify-center p-6"></div>;
  }

  return (
    <div className="mt-3 min-h-screen space-y-14">
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <h1 className="flex items-center gap-2 text-3xl font-bold capitalize text-blue-500">
            {t("welcome")} {user?.name?.split(" ")[0]}
          </h1>

          {/* <div className="flex gap-4">
            <div className="relative">
              <span className="absolute -left-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-orange-500 text-xs text-white">
                1
              </span>
              <button className="flex min-w-36 items-center gap-3 rounded-lg bg-blue-500 px-4 py-2 text-center text-sm font-medium text-white shadow">
                <QuizzesStudent className="mt-[-3px] h-5 w-5" />
                <span className="flex-1 ltr:text-left rtl:text-right">{t("quizzes")}</span>
              </button>
            </div>
            <div className="relative">
              <span className="absolute -left-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                1
              </span>
              <button className="flex min-w-36 items-center gap-3 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white shadow">
                <HomeworksStudent className="h-5 w-5" />
                {t("homeworks")}
              </button>
            </div>
          </div> */}
        </div>

        {/* Course Card */}
        <div className="flex gap-2">
          <div className="relative flex flex-1 items-center justify-between space-x-4 rounded-3xl bg-blue-500 p-5 px-6 text-white shadow-md">
            {/* Cartoon characters */}
            <div className="absolute top-0 w-72 ltr:left-0 rtl:right-0">
              <Lottie animationData={animationData} autoplay loop={false} />
            </div>
            <div className="w-56"></div>

            {/* Course Info */}
            <div className="flex-1 px-4">
              <h2 className="text-2xl font-bold">{courseDetails?.course}</h2>
              <p className="line-clamp-2 text-sm text-slate-200">{courseDetails?.description}</p>
            </div>
          </div>
          {/* Progress Meter */}
          <div className="flex w-20 flex-col items-center justify-center rounded-3xl bg-blue-500 px-[10px] py-[10px] shadow-md">
            <div className="relative w-full flex-1 overflow-hidden rounded-2xl bg-white">
              {/* Label */}
              <div className="absolute left-1/2 z-30 mt-3 -translate-x-1/2 font-semibold text-orange-500">
                {completionPercentage}%
              </div>

              {/* Wave Fill */}
              <div className="absolute left-0 w-full">
                <Wave
                  fill="#1bc244"
                  paused={false}
                  options={{
                    height: 96 - animatedHeight,
                    amplitude: 5,
                    speed: 0.45,
                    points: 3,
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-11 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        {studentLessons.completion.map((item, index) => {
          const lesson = item.lesson;
          const challengeDetails = item.challenge_details;
          if (!lesson) return null;

          // Determine type
          let type = null;
          if (item.is_challenge) type = "challenge";
          else if (item.is_concept_lesson) type = "lesson";
          else if (item.is_graduation_project) type = "graduation";
          else if (item.is_code) type = "code";
          else if (item.is_game) type = "game";
          else if (item.is_project) type = "project";

          const isCompleted = item.is_completed;
          const isFinished = item.finished_by_teacher;
          const isLocked = item.is_locked;
          const isActive = item.is_active;
          const isOrdered = item.is_ordered;
          const prevLesson = studentLessons?.completion[index - 1];

          // Unified numbering logic based only on is_ordered
          let prefixLabel = "";
          if (isOrdered) {
            numberCount += 1;
            prefixLabel = `${numberCount}`;
          }

          return (
            <LearningCard
              key={lesson.id}
              title={lesson.title}
              id={lesson.id}
              has_recap_video={lesson.has_recap}
              image={lesson.image}
              desc={item?.description}
              type={type}
              isLocked={isLocked}
              isActive={isActive}
              isOrdered={isOrdered}
              isFinished={isFinished}
              isCompleted={isCompleted}
              lastIsActiveLesson={lastIsActiveLesson}
              courseName={courseDetails?.course}
              challengeDetails={challengeDetails}
              prevLesson={prevLesson}
              navigate={navigate}
              openSpinWheelModal={openSpinWheelModal}
              numberLabel={prefixLabel}
            />
          );
        })}
      </div>

      <SpinWheelModal ref={spinWheelModalRef} onSubmit={handleSpinWheelSuccess} />
    </div>
  );
};

export default StudentLearningArea;
